import { ApolloClient } from '@apollo/client';
import React, { PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import { Reference } from 'sr-types/lib/search/v1/graphql';
import { UserContext } from '../../auth/UserContext';
import { failedToastMessage, successToastMessage } from '../../utils/commonUtils';
import {
    DEFAULT_SHARE_ACTION,
    DEFAULT_SHARE_SETTINGS,
    DEFAULT_SHARE_SETTINGS_FORM_STATE,
    SHARE_SETTINGS_MODE
} from '../constants';
import { useSaveShareSettingsApi } from '../data/useSaveShareSettingsApi';
import type {
    InitializeShareSettingsScopePayload,
    ShareSettingsInternalProviderContextValue,
    ShareSettingsMode,
    ShareSettingsPublicProviderContextValue,
    ShareSettingsScopeId,
    ShareSettingsScopeState,
    ShareSettingsViewModel
} from '../types';
import { ShareSettingsContext } from './components/ShareSettingsContext';
import { ShareSettingsInternalContext } from './components/ShareSettingsInternalContext';
import {
    makeInitialShareSettingsState,
    makeShareAccessEntryForEntityRecordOwner,
    makeShareAccessEntryForLoggedInUser
} from './utils';

export type ShareSettingsProviderProps = PropsWithChildren<{
    throwOnError?: boolean;
    client: ApolloClient<object>;
    enableNotification?: boolean;
}>;

export function ShareSettingsProvider({
    throwOnError = false,
    client,
    enableNotification = true,
    children
}: ShareSettingsProviderProps) {
    const { userProfile } = useContext(UserContext);

    const [scopeStateMap, setScopeStateMap] = useState<Record<ShareSettingsScopeId, ShareSettingsScopeState>>({});

    const { saveShareSettingsApi, isSavingShareSettings } = useSaveShareSettingsApi({
        client,
        enableNotification
    });

    const makeNormalizedShareSettings = useCallback((value: ShareSettingsViewModel): ShareSettingsViewModel => {
        const shareAccessEntryForEntityRecordOwner = makeShareAccessEntryForEntityRecordOwner(value);

        return makeInitialShareSettingsState(value, shareAccessEntryForEntityRecordOwner);
    }, []);

    const makeScopeState = useCallback<
        (mode: ShareSettingsMode, value?: ShareSettingsViewModel) => ShareSettingsScopeState
    >(
        (mode, value) => {
            const safeValue = value ?? DEFAULT_SHARE_SETTINGS;
            const shareAccessEntryForEntityRecordOwner = makeShareAccessEntryForEntityRecordOwner(safeValue);
            const shareAccessEntryForLoggedInUser = makeShareAccessEntryForLoggedInUser(safeValue, userProfile);
            const normalizedShareSettings = makeNormalizedShareSettings(safeValue);

            if (mode === SHARE_SETTINGS_MODE.create) {
                return {
                    mode: SHARE_SETTINGS_MODE.create,
                    committedShareSettings: normalizedShareSettings,
                    currentShareSettings: normalizedShareSettings,
                    shareAccessEntryForEntityRecordOwner,
                    shareAccessEntryForLoggedInUser,
                    backedUpSharedWith: [],
                    backedUpSharedWithDepartments: [],
                    shareSettingsFormState: {
                        defaultAction: DEFAULT_SHARE_ACTION,
                        actorsToAddToSharedWith: [],
                        departmentsToAddToSharedWith: []
                    }
                };
            }

            return {
                mode: SHARE_SETTINGS_MODE.update,
                initialShareSettings: normalizedShareSettings,
                currentShareSettings: normalizedShareSettings,
                shareAccessEntryForEntityRecordOwner,
                shareAccessEntryForLoggedInUser,
                backedUpSharedWith: [],
                backedUpSharedWithDepartments: [],
                shareSettingsFormState: {
                    defaultAction: DEFAULT_SHARE_ACTION,
                    actorsToAddToSharedWith: [],
                    departmentsToAddToSharedWith: []
                }
            };
        },
        [makeNormalizedShareSettings, userProfile]
    );

    const initializeShareSettingsScope = useCallback(
        (scopeId: ShareSettingsScopeId, payload: InitializeShareSettingsScopePayload) => {
            setScopeStateMap((current) => {
                if (payload.mode === SHARE_SETTINGS_MODE.create && current[scopeId]) {
                    return current;
                }

                const nextScopeState =
                    payload.mode === SHARE_SETTINGS_MODE.update
                        ? makeScopeState(SHARE_SETTINGS_MODE.update, payload.initialShareSettings)
                        : makeScopeState(SHARE_SETTINGS_MODE.create);

                return {
                    ...current,
                    [scopeId]: nextScopeState
                };
            });
        },
        [makeScopeState]
    );

    const commitCreatedShareSettings = useCallback((scopeId: ShareSettingsScopeId) => {
        setScopeStateMap((current) => {
            const scopeState = current[scopeId];

            if (!scopeState || scopeState.mode !== SHARE_SETTINGS_MODE.create) {
                return current;
            }

            return {
                ...current,
                [scopeId]: {
                    ...scopeState,
                    committedShareSettings: scopeState.currentShareSettings
                }
            };
        });
    }, []);

    const resetShareSettings = useCallback(
        (scopeId: ShareSettingsScopeId) => {
            setScopeStateMap((current) => {
                const scopeState = current[scopeId];

                if (!scopeState) {
                    return current;
                }

                if (scopeState.mode === SHARE_SETTINGS_MODE.create) {
                    const normalizedDefaultSettings = makeNormalizedShareSettings(DEFAULT_SHARE_SETTINGS);

                    return {
                        ...current,
                        [scopeId]: {
                            ...scopeState,
                            committedShareSettings: normalizedDefaultSettings,
                            currentShareSettings: normalizedDefaultSettings,
                            backedUpSharedWith: [],
                            backedUpSharedWithDepartments: [],
                            shareSettingsFormState: DEFAULT_SHARE_SETTINGS_FORM_STATE
                        }
                    };
                }

                return {
                    ...current,
                    [scopeId]: {
                        ...scopeState,
                        currentShareSettings: scopeState.initialShareSettings,
                        backedUpSharedWith: [],
                        backedUpSharedWithDepartments: [],
                        shareSettingsFormState: DEFAULT_SHARE_SETTINGS_FORM_STATE
                    }
                };
            });
        },
        [makeNormalizedShareSettings]
    );

    const saveShareSettings = useCallback(
        async (scopeId: ShareSettingsScopeId, entityReference: Reference, deepLinkURL?: string) => {
            const scopeState = scopeStateMap[scopeId];

            if (!scopeState) {
                if (throwOnError) {
                    throw new Error(`No share settings scope found for id ${scopeId}`);
                } else {
                    failedToastMessage('common.share.settings.update.failure');
                    return;
                }
            }

            const settingsToSave =
                scopeState.mode === SHARE_SETTINGS_MODE.create
                    ? scopeState.committedShareSettings
                    : scopeState.currentShareSettings;

            const existingSharedWithAccountIds =
                scopeState.mode === SHARE_SETTINGS_MODE.update
                    ? (scopeState.initialShareSettings.sharedWith ?? []).map((shareAccess) => shareAccess.actor.id)
                    : [];

            try {
                await saveShareSettingsApi(
                    entityReference,
                    settingsToSave,
                    userProfile,
                    existingSharedWithAccountIds,
                    deepLinkURL
                );

                setScopeStateMap((current) => {
                    const currentScopeState = current[scopeId];

                    if (!currentScopeState) {
                        return current;
                    }

                    const updatedScopeState: ShareSettingsScopeState =
                        currentScopeState.mode === SHARE_SETTINGS_MODE.create
                            ? {
                                  ...currentScopeState,
                                  committedShareSettings: makeNormalizedShareSettings(DEFAULT_SHARE_SETTINGS),
                                  currentShareSettings: makeNormalizedShareSettings(DEFAULT_SHARE_SETTINGS),
                                  backedUpSharedWith: [],
                                  backedUpSharedWithDepartments: [],
                                  shareSettingsFormState: DEFAULT_SHARE_SETTINGS_FORM_STATE
                              }
                            : {
                                  ...currentScopeState,
                                  initialShareSettings: makeNormalizedShareSettings(settingsToSave),
                                  backedUpSharedWith: [],
                                  backedUpSharedWithDepartments: [],
                                  shareSettingsFormState: DEFAULT_SHARE_SETTINGS_FORM_STATE
                              };

                    return {
                        ...current,
                        [scopeId]: updatedScopeState
                    };
                });

                if (!throwOnError) successToastMessage('common.share.settings.update.success');
            } catch (err) {
                if (throwOnError) throw err;
                failedToastMessage('common.share.settings.update.failure');
            }
        },
        [makeNormalizedShareSettings, saveShareSettingsApi, scopeStateMap, throwOnError, userProfile]
    );

    const updateShareSettingsState = useCallback<ShareSettingsInternalProviderContextValue['updateShareSettingsState']>(
        (scopeId, settings) => {
            setScopeStateMap((current) => {
                const currentScopeState = current[scopeId];

                if (!currentScopeState) {
                    return current;
                }

                return {
                    ...current,
                    [scopeId]: {
                        ...currentScopeState,
                        currentShareSettings: {
                            ...currentScopeState.currentShareSettings,
                            ...settings
                        }
                    }
                };
            });
        },
        []
    );

    const setBackedUpSharedWith = useCallback<ShareSettingsInternalProviderContextValue['setBackedUpSharedWith']>(
        (scopeId, value) => {
            setScopeStateMap((current) => {
                const currentScopeState = current[scopeId];

                if (!currentScopeState) {
                    return current;
                }

                const resolvedBackedUpSharedWith =
                    typeof value === 'function' ? value(currentScopeState.backedUpSharedWith) : value;

                return {
                    ...current,
                    [scopeId]: {
                        ...currentScopeState,
                        backedUpSharedWith: resolvedBackedUpSharedWith
                    }
                };
            });
        },
        []
    );

    const setBackedUpSharedWithDepartments = useCallback<
        ShareSettingsInternalProviderContextValue['setBackedUpSharedWithDepartments']
    >((scopeId, value) => {
        setScopeStateMap((current) => {
            const currentScopeState = current[scopeId];

            if (!currentScopeState) {
                return current;
            }

            const resolvedBackedUpSharedWithDepartments =
                typeof value === 'function' ? value(currentScopeState.backedUpSharedWithDepartments) : value;

            return {
                ...current,
                [scopeId]: {
                    ...currentScopeState,
                    backedUpSharedWithDepartments: resolvedBackedUpSharedWithDepartments
                }
            };
        });
    }, []);

    const setShareSettingsFormState = useCallback<
        ShareSettingsInternalProviderContextValue['setShareSettingsFormState']
    >((scopeId, value) => {
        setScopeStateMap((current) => {
            const currentScopeState = current[scopeId];

            if (!currentScopeState) {
                return current;
            }

            const resolvedFormState =
                typeof value === 'function' ? value(currentScopeState.shareSettingsFormState) : value;

            return {
                ...current,
                [scopeId]: {
                    ...currentScopeState,
                    shareSettingsFormState: resolvedFormState
                }
            };
        });
    }, []);

    const publicContextValue = useMemo<ShareSettingsPublicProviderContextValue>(
        () => ({
            scopes: scopeStateMap,
            saveShareSettings,
            commitCreatedShareSettings: commitCreatedShareSettings,
            resetShareSettings,
            isSavingShareSettings
        }),
        [commitCreatedShareSettings, isSavingShareSettings, resetShareSettings, saveShareSettings, scopeStateMap]
    );

    const internalContextValue = useMemo<ShareSettingsInternalProviderContextValue>(() => {
        const internalContextValue: ShareSettingsInternalProviderContextValue = {
            scopes: scopeStateMap,
            enableNotification,
            initializeShareSettingsScope,
            updateShareSettingsState,
            setBackedUpSharedWith,
            setBackedUpSharedWithDepartments,
            setShareSettingsFormState
        };

        return internalContextValue;
    }, [
        enableNotification,
        initializeShareSettingsScope,
        scopeStateMap,
        setBackedUpSharedWith,
        setBackedUpSharedWithDepartments,
        setShareSettingsFormState,
        updateShareSettingsState
    ]);

    return (
        <ShareSettingsContext value={publicContextValue}>
            <ShareSettingsInternalContext value={internalContextValue}>{children}</ShareSettingsInternalContext>
        </ShareSettingsContext>
    );
}
